// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import { GridOffset } from "@insite/mobius/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Star from "@insite/mobius/Icons/Star";
import { actionStyles } from "@sc/Overrides/Widgets/MyListsDetails/MyListsDetailsActions";
import StarX from "@sc/XDL/Icons/StarX";
import { color, modalButtonsActionStyles, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

actionStyles.wrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        gap: 22px;
        border-bottom: 1px solid ${color("common.border")};
        margin-bottom: 12px;
        padding-bottom: 12px;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
            gap: 0;
        }
    `,
};

actionStyles.actionsWrapperRight = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 12px;
        max-width: 50%;
        ${xScreen("lte", "sm")} {
            max-width: 100%;
        }
    `,
};

actionStyles.buttonWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 15px;
        ${xScreen("lte", "md")} {
            justify-content: flex-end;
        }
        ${xScreen("lte", "sm")} {
            margin-top: 20px;
            flex-direction: column;
            align-items: flex-start;
        }
    `,
};

actionStyles.listInfoWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 76px;
        ${xScreen("lte", "sm")} {
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: flex-start;
        }
    `,
};

actionStyles.innerButtonWrapper = {
    css: css`
        display: flex;
        gap: 24px;
        align-items: end;
        flex-wrap: wrap;
        ${xScreen("lte", "xs")} {
            width: 100%;
        }
    `,
};

actionStyles.statusContainer = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-bottom: 10px;
    `,
};

actionStyles.addItemButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 8px 24px;
    `,
    variant: "secondary",
};
actionStyles.removeSelectedButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 0 15px;
    `,
    variant: "secondary",
};
actionStyles.addListButton = {
    css: css`
        width: fit-content;
        padding: 0 15px;
        white-space: nowrap;
    `,
};

actionStyles.editListModal = {
    sizeVariant: "small",
};
actionStyles.copyListModal = {
    sizeVariant: "small",
};

actionStyles.actionsWrapperLeft = {
    css: css`
        max-width: 50%;
        ${xScreen("lte", "sm")} {
            max-width: none;
        }
    `,
};

actionStyles.listActionsWrapper = {
    css: css`
        margin-bottom: 12px;
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        ${StyledButton}:hover {
            text-decoration: underline;
        }
    `,
};

actionStyles.nameText = {
    variant: "h1",
    color: "primary",
    css: css`
        display: block;
        margin-bottom: 16px;
        ${xScreen("lte", "md")} {
            max-width: 100%;
        }
    `,
};

actionStyles.wishlistCountWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        ${xScreen("lte", "sm")} {
            flex-direction: row;
            gap: 10px;
        }
    `,
};

actionStyles.wishlistLabel = {
    size: 14,
    lineHeight: "22px",
};

actionStyles.wishlistCount = {
    size: 16,
    weight: "bold",
    color: "text",
};

actionStyles.iconContainer = {
    css: css`
        width: fit-content;
    `,
};
actionStyles.actionFavoriteClickable = {
    css: css`
        font-size: 12px;
        width: fit-content;
        line-height: 22px;
    `,
};
actionStyles.starIcon = {
    src: Star,
    size: 12,
    css: css`
        margin-right: 6px;
    `,
};
actionStyles.starXIcon = {
    src: StarX,
    size: 12,
    color: "primary.main",
    css: css`
        margin-right: 6px;
    `,
};

actionStyles.overflowMenu = {
    _width: 140,
    cssOverrides: {
        wrapper: css`
            width: auto;
            height: auto;
            & button[aria-expanded="true"] ${IconWrapper} svg {
                transform: rotate(180deg);
            }
            @media print {
                display: none;
            }
        `,
        menu: css`
            padding: 4px 0;
            li:last-child button {
                padding-bottom: 8px;
                &::after {
                    display: none;
                }
            }
        `,

        menuItem: css`
            padding: 8px 12px 0;
            font-size: 12px;
            line-height: 17px;
            color: ${color("common.ui4")};
            font-weight: bold;
            &::after {
                content: "";
                display: block;
                border-bottom: 1px solid ${color("common.border")};
                width: calc(100% - 12px);
                margin-top: 8px;
            }
            &:hover {
                background: ${color("common.ui8")};
            }
        `,
    },
};

actionStyles.moreActions = {
    icon: {
        iconProps: {
            src: ChevronDown,
            size: 14,
        },
        position: "right",
    },
};

const customClickable = {
    css: css`
        font-size: 12px;
        line-height: 17px;
        font-weight: bold;
        color: ${color("text.link")};
        padding: 0;
        margin: 0;
    `,
};
actionStyles.printButton = {
    ...customClickable,
};
actionStyles.addTagsButton = {
    ...customClickable,
};
actionStyles.scheduleButton = {
    ...customClickable,
};
actionStyles.editButton = {
    ...customClickable,
};
actionStyles.shareButton = {
    ...customClickable,
};
actionStyles.hideButton = {
    ...customClickable,
};
actionStyles.copyButton = {
    ...customClickable,
};
actionStyles.exportButton = {
    ...customClickable,
};
actionStyles.deleteButton = {
    ...customClickable,
};

actionStyles.printListModal = {
    productPropertiesCheckboxGridContainer: {
        gap: 0,
        css: css`
            ${GridOffset} {
                gap: 10px;

                ${xScreen("lte", "sm")} {
                    gap: 0;
                }
            }
            ${GridItemStyle} {
                padding: 10px 0;
            }
        `,
    },
    barcodeTypeRadioGroup: {
        horizontal: true,
        css: css`
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                align-items: baseline;
                gap: 10px;
            }
        `,
    },
    productPropertiesCheckboxGridItem: {
        width: 12,
        css: css`
            gap: 15px;

            ${xScreen("lte", "sm")} {
                flex-direction: column;
                align-items: baseline;
            }
        `,
    },
    buttonsGridItem: {
        ...modalButtonsActionStyles.buttonsGridItem,
    },
    cancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
    submitButton: {
        color: "primary",
        ...modalButtonsActionStyles.submitButton,
    },
};
